import { useContext, useEffect } from 'react'
import usePrevious from '../hooks/usePrevious'
import { UserContext, UserContextType } from '../UserContext'
import { selectDefaultOutputDevice } from './MediaDevices'

/**
 * Listens for changes to the `state.mediaSources` and choose a new device and/or
 * perform other jobs.
 */
export default function useAutoSelectOnMediaDeviceDisconnect() {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const audioOutputs = state.mediaSources.audioOutputs
    const currAudioOutputSelection = state.mediaSelection.audioOutput
    const globalOutputDevice = state.mediaSelection.globalOutputDevice

    const prevAudioOutputs = usePrevious(state.mediaSources.audioOutputs)

    /**
     * If the current output selection is not found in the new mediaSources.audioOutputs list,
     * then select _undef. Without setting to _undef, a new device is selected, then there is
     * no sound output.
     */
    useEffect(() => {
        console.log(
            `useAutoSelectOnMediaDeviceDisconnect: currAudioOutputSelection=` +
                `${currAudioOutputSelection} currAudioOutputSources=%O`,
            audioOutputs
        )

        if (!state.mediaSelection.isDefaultMediaSelectionLoaded) {
            console.log(
                `useAutoSelectOnMediaDeviceDisconnect: no-op because "mediaSelection.isDefaultLoaded" is false..`
            )
            return
        }
        if (JSON.stringify(audioOutputs) === JSON.stringify(prevAudioOutputs)) {
            return
        }

        // If currAudioOutputSelection cannot be found in the currAudioOutputSources list:
        if (audioOutputs.filter((s) => s.deviceId === currAudioOutputSelection).length === 0) {
            console.log(
                `useAutoSelectOnMediaDeviceDisconnect: currAudioOutputSelection=` +
                    `${currAudioOutputSelection} not found in the currAudioOutputSources list.` +
                    `Selecting an output.`
            )

            const defaultOutputDevice = selectDefaultOutputDevice(state.mediaSources.audioOutputs)
            if (defaultOutputDevice) {
                dispatch({
                    type: 'update_media_selection',
                    value: { audioOutput: '_none' },
                })
                // disgusting but necessary
                setTimeout(() => {
                    dispatch({
                        type: 'update_media_selection',
                        value: { audioOutput: defaultOutputDevice.deviceId },
                    })
                }, 250)
            } else {
                dispatch({
                    type: 'update_media_selection',
                    value: { audioOutput: '_none' },
                })
            }
        }
    }, [
        audioOutputs,
        currAudioOutputSelection,
        globalOutputDevice,
        state.mediaSources.audioOutputs,
        state.mediaSelection.isDefaultMediaSelectionLoaded,
        dispatch,
    ])
}
